import React, { createContext, useContext, useState, useEffect } from 'react';

interface SidebarContextType {
  isOpen: boolean;
  isMini: boolean;
  isMobile: boolean;
  toggleSidebar: () => void;
  toggleMiniSidebar: () => void;
  closeSidebar: () => void;
  openSidebar: () => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within SidebarProvider');
  }
  return context;
};

interface SidebarProviderProps {
  children: React.ReactNode;
}

export const SidebarProvider: React.FC<SidebarProviderProps> = ({ children }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [isOpen, setIsOpen] = useState(window.innerWidth >= 1024);
  const [isMini, setIsMini] = useState(() => {
    const saved = localStorage.getItem('sidebar_mini_mode');
    return saved ? JSON.parse(saved) : false;
  });

  // Persist isMini state to localStorage
  useEffect(() => {
    localStorage.setItem('sidebar_mini_mode', JSON.stringify(isMini));
  }, [isMini]);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 1024;
      const wasMobile = isMobile;
      
      setIsMobile(mobile);
      
      // Only auto-adjust sidebar when transitioning between mobile/desktop
      if (wasMobile !== mobile) {
        if (mobile) {
          // Transitioning to mobile: close sidebar
          setIsOpen(false);
          setIsMini(false);
        } else {
          // Transitioning to desktop: open sidebar
          setIsOpen(true);
          setIsMini(false);
        }
      }
    };

    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [isMobile]);

  const toggleSidebar = () => setIsOpen(!isOpen);
  const toggleMiniSidebar = () => {
    if (isMobile) {
      // On mobile, toggle sidebar open/close instead of mini mode
      setIsOpen(!isOpen);
    } else {
      // On desktop, toggle mini mode
      setIsMini(!isMini);
    }
  };
  const closeSidebar = () => setIsOpen(false);
  const openSidebar = () => setIsOpen(true);

  return (
    <SidebarContext.Provider
      value={{
        isOpen,
        isMini,
        isMobile,
        toggleSidebar,
        toggleMiniSidebar,
        closeSidebar,
        openSidebar,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
};